/* eslint-disable import/no-duplicates */
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import BarGroup from 'components/bar-group';
import ScrollableChart from 'components/scrollable-chart';
import {OrientationEnum} from 'common/proxies/enum-proxy';
import {CURRENCY} from 'constants/value-type';
import {NUMERIC} from 'constants/value-type';
import {PERCENTAGE} from 'constants/value-type';
import {getFilteredSeries, getParsedGroups} from 'utils/data-util';
import styles from './index.css';

export const getChartHeight = (tickRange, numItems, axisHeight) =>
  Math.ceil(tickRange * numItems) + axisHeight;

export const getNumItems = (groups) =>
  groups.length > 0
    ? Math.max.apply(
        null,
        groups
          .map((group) => (Array.isArray(group.series) ? group.series : []))
          .reduce((previous, current) => previous.concat(current))
          .map((series) => series.data.length),
      )
    : 0;

const BarChart = ({
  barHeight,
  groups,
  height,
  showAxis,
  showLegend,
  showTooltip,
  valueType,
  verticalSpacing,
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [chartWidth, setChartWidth] = useState(0);
  const axisHeight = 18;
  const legendHeight = 24;

  const parsedGroups = getParsedGroups(groups);

  // Max number of data items in any series:
  const numItems = getNumItems(parsedGroups);
  const tickRange = barHeight + verticalSpacing;

  const chartHeight = getChartHeight(tickRange, numItems, axisHeight);
  const interactionHeight = Math.ceil(tickRange * numItems);
  const tooltipY = barHeight * activeIndex;

  const interactionConfig = {
    direction: OrientationEnum.VERTICAL.id,
    height: interactionHeight,
    width: chartWidth,
    tickRange,
    y: axisHeight,
  };
  const legendConfig = {
    height: legendHeight,
    series: getFilteredSeries(parsedGroups, 'excludeFromLegend'),
  };
  const tooltipConfig = {
    series: getFilteredSeries(parsedGroups, 'excludeFromTooltip'),
    y: tooltipY,
  };
  return (
    <ScrollableChart
      className={styles.BarChart}
      configuration={{
        interaction: interactionConfig,
        legend: legendConfig,
        tooltip: tooltipConfig,
      }}
      containerHeight={Math.ceil(height)}
      height={chartHeight}
      onInteraction={(interaction) => setActiveIndex(interaction.activeIndex)}
      setWidth={setChartWidth}
      showLegend={showLegend}
      showTooltip={showTooltip}>
      {parsedGroups.map((group, index) => (
        <BarGroup
          activeIndex={activeIndex}
          axisHeight={axisHeight}
          barHeight={barHeight}
          group={group}
          key={`barGroup${index}`}
          showAxis={showAxis}
          valueType={valueType}
          verticalSpacing={verticalSpacing}
          width={chartWidth}
        />
      ))}
    </ScrollableChart>
  );
};
BarChart.propTypes = {
  barHeight: PropTypes.number,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      series: PropTypes.array.isRequired,
    }),
  ).isRequired,
  height: PropTypes.number,
  showAxis: PropTypes.bool,
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
  valueType: PropTypes.oneOf([CURRENCY, NUMERIC, PERCENTAGE]),
  verticalSpacing: PropTypes.number,
};

BarChart.defaultProps = {
  barHeight: 30,
  height: 0,
  valueType: NUMERIC,
  verticalSpacing: 6,
};

export default BarChart;
